import {
  ModalCloseButton,
  ModalHeader,
  ModalOverlay,
  UseDisclosureReturn,
  useToast,
} from "@chakra-ui/react";
import { Operation } from "@syla/shared/types/requests/BulkEditRequest";
import React, { FormEvent, useCallback, useMemo, useState } from "react";
import { filterToFilterParams } from "../../../../../api/order/filterToFilterParams";
import { useReportError } from "../../../../../helpers/useReportError";
import { useCurrentAccountStore } from "../../../../../store/currentAccountStore";
import { useMutationBulkEdit } from "../../../../../store/useMutationBulkEdit";
import { OrderFilterSetting } from "../../../../../types/order/orderQuery";
import { AppModal } from "../../../../modals/AppModal";
import { AppModalContent } from "../../../../modals/AppModalContent";
import {
  validateOperation,
  BulkEditConfigureOperationStep,
} from "./BulkEditConfigureOperationStep";
import { BulkEditSelectOptionStep } from "./BulkEditSelectOptionStep";
import { bulkEditOperations, OperationEntry } from "./common";

export type BulkEditOptionType = OperationEntry["type"];

type BulkEditModalProps = {
  disclosure: UseDisclosureReturn;
  filters: OrderFilterSetting;
  selectedCount?: number;
  operationType?: BulkEditOptionType;
  selectedIds: string[];
  clearSelected: () => void;
};

export function BulkEditModal({
  disclosure,
  filters,
  selectedCount,
  operationType,
  selectedIds,
  clearSelected,
}: BulkEditModalProps): React.ReactElement {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedOperationType, setSelectedOperationType] = useState<
    BulkEditOptionType | undefined
  >(operationType);

  const [operations, setOperations] = useState<OperationEntry[]>([]);

  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const mutationBulkEdit = useMutationBulkEdit(accountId);

  const toast = useToast();

  const reportError = useReportError();

  const operationsValid = useMemo(
    () => operations.every(validateOperation),
    [operations],
  );

  const canSubmit = operations.length > 0 && operationsValid;

  const onBack = useCallback(() => {
    setSelectedOperationType(undefined);
    setOperations([]);
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      try {
        setIsSubmitting(true);

        const { updatedCount } = await mutationBulkEdit.mutateAsync({
          filters: filterToFilterParams(filters),
          groupIds: selectedIds,
          operations: operations as Operation[],
        });

        toast({
          status: "success",
          variant: "subtle",
          position: "bottom",
          title: `${updatedCount.toLocaleString()} transactions updated`,
        });

        clearSelected();
        disclosure.onClose();
      } catch (error) {
        reportError(error, {
          title: "Bulk edit failed",
        });

        setIsSubmitting(false);
      }
    },
    [
      mutationBulkEdit,
      clearSelected,
      disclosure,
      filters,
      operations,
      reportError,
      selectedIds,
      toast,
    ],
  );

  const modalHeader =
    selectedOperationType ?
      bulkEditOperations[selectedOperationType].label
    : "Bulk edit transactions";

  return (
    <AppModal
      isOpen={disclosure.isOpen}
      onClose={disclosure.onClose}
      closeOnOverlayClick={!isSubmitting}
      closeOnEsc={!isSubmitting}
      size={selectedOperationType ? "3xl" : "xl"}
    >
      <ModalOverlay />

      <AppModalContent as="form" noValidate onSubmit={handleSubmit}>
        <ModalHeader>{modalHeader}</ModalHeader>
        <ModalCloseButton isDisabled={isSubmitting} />

        {!selectedOperationType ?
          <BulkEditSelectOptionStep
            onSelectOperation={setSelectedOperationType}
            onCancel={disclosure.onClose}
            transactionCount={selectedCount || 0}
          />
        : <BulkEditConfigureOperationStep
            operationType={selectedOperationType}
            operations={operations}
            setOperations={setOperations}
            selectedIds={selectedIds}
            filters={filters}
            selectedCount={selectedCount}
            canSubmit={canSubmit}
            isSubmitting={isSubmitting}
            onBack={onBack}
          />
        }
      </AppModalContent>
    </AppModal>
  );
}
